import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useRouter } from 'next/router'
import { Icon, Context as ContextAppFrame } from 'scala'
import React, { ReactElement, useCallback, useContext, useMemo } from 'react'
import { useEffectOnce, useInterval } from 'react-use'
import { ModalConfirmation } from '../../../components'
import { PlaylistItem } from '../../../components/playlist/playlist-item'
import { useFetchPlaylists } from '../../../hooks/playlist/use-fetch-playlists'
import { usePlaylist } from '../../../hooks/playlist/use-playlist'
import { Playlist } from '../../../types'
import { ActionsPlaylist } from '../actions-playlist'
import { DeletePlaylist } from '../delete-playlist'
import { EditPlaylist } from '../edit-playlist'
import { Empty } from './empty'
import styles from './playlists-sidebar.module.scss'
import { Skeleton } from './skeleton'
import { UnsubscribePlaylist } from '../unsubscribe-playlist'
import { usePageVisibility } from '../../../hooks/misc/use-page-visibility'
import { Context } from '../../../context'
import { getCustomAttributesPlaylist } from './utils'

export const PlaylistsSidebar: React.FC = (): ReactElement => {
  const { user } = useContext(ContextAppFrame)
  const {
    failedAuthentication: { disableFetching }
  } = useContext(Context)
  const { i18n } = useLingui()
  const { pathname } = useRouter()
  const { playlists, fetchPlaylists } = useFetchPlaylists({ asGuest: true })
  const pageIsActive = usePageVisibility()
  const isNotLibraryPage = useMemo(
    () => !pathname.includes('/library'),
    [pathname]
  )

  const {
    addTaskToPlaylist,
    createPlaylist,
    openModalConfirmation,
    onDismissModalConfirmation,
    onConfirmAddTaskToPlaylist
  } = usePlaylist()

  const onCreatePlaylist = useCallback(
    () =>
      createPlaylist({
        qtdPlaylists: playlists?.length,
        events: {
          source: pathname.includes('/playlist') ? 'playlist' : 'library',
          first_playlist_created: playlists?.length === 0
        }
      }),
    [pathname, playlists, createPlaylist]
  )

  useEffectOnce(() => {
    fetchPlaylists()
  })

  useInterval(
    () => {
      fetchPlaylists()
    },
    disableFetching || !pageIsActive ? null : isNotLibraryPage ? 60000 : 30000
  )

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <p className={styles.title}>{i18n._(t`library.playlists`)}</p>

        <button
          id="new_playlist_button"
          type="button"
          onClick={onCreatePlaylist}
          className={styles.button}
        >
          <Icon name="plus" width={20} height={20} className={styles.icon} />{' '}
          {i18n._(t`library.new.playlist`)}
        </button>
      </div>

      <div className={styles.list}>
        {!playlists && <Skeleton />}

        {playlists && playlists.length === 0 && (
          <Empty onCreatePlaylist={onCreatePlaylist} />
        )}

        {playlists &&
          playlists.map((playlist: Playlist, i: number) => {
            const isOwner = playlist.creator.id === user.id
            return (
              <PlaylistItem
                index={i}
                key={playlist.id}
                {...playlist}
                disableDrop={!isOwner}
                onSongDropped={addTaskToPlaylist}
                isShared={playlist.isShared || !!playlist.guests.totalCount}
                ActionsPlaylist={
                  <ActionsPlaylist
                    small
                    right
                    events={getCustomAttributesPlaylist(playlist, {
                      source: 'playlist_tab',
                      user_type: isOwner ? 'owner' : 'guest'
                    })}
                    playlistId={playlist.id}
                    UnsubscribePlaylist={!isOwner && <UnsubscribePlaylist />}
                    DeletePlaylist={isOwner && <DeletePlaylist />}
                    EditPlaylist={
                      isOwner && (
                        <EditPlaylist
                          title={playlist.name}
                          description={playlist.description}
                        />
                      )
                    }
                  />
                }
              />
            )
          })}
      </div>

      {openModalConfirmation && (
        <ModalConfirmation
          loading={false}
          isOpen={openModalConfirmation}
          onDismiss={onDismissModalConfirmation}
          onConfirm={onConfirmAddTaskToPlaylist}
          title={i18n._(t`song.already.playlist.title`)}
          info={i18n._(t`song.already.playlist.description`)}
          confirmLabel={i18n._(t`song.already.playlist.cta2`)}
          cancelLabel={i18n._(t`song.already.playlist.cta1`)}
          loadingLabel={i18n._(t`label.loading`)}
        />
      )}
    </div>
  )
}
